import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, CloudOff, Loader2, Plus, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Field, SelectField, PageHeader } from "@/components/common";
import { EquipmentDialog } from "@/components/v3forms";
import { BillingPanel } from "@/components/billing/BillingPanel";
import { api, formatApiError } from "@/lib/api";
import { useList } from "@/lib/hooks";
import { INTERVENTION_TYPES, PRIORITIES, EQUIPMENT_TYPES, CONTRACT_TYPES, MODES, fmtMin, fmtDateTime, fmtDate, todayStr } from "@/lib/constants";
import { ModeBlock, modeOf } from "@/components/ModeFields";

const STEPS = ["Client", "Équipement", "Planification", "Travaux", "Validation"];
const FIELDS = ["client_id", "title", "type", "scheduled_at", "duration_minutes", "technician_id", "description", "diagnostic", "report", "location",
  "client_summary", "notify_client", "priority", "internal_notes", "equipment_ids", "contract_id", "mode", "workshop", "share_pickup_address"];
const EMPTY = { client_id: "", title: "", type: "depannage", scheduled_at: "", duration_minutes: 60, technician_id: null, description: "", diagnostic: "",
  report: "", location: "", client_summary: "", notify_client: true, priority: "normale", internal_notes: "", equipment_ids: [], contract_id: null };
const pick = (f) => Object.fromEntries(FIELDS.map((k) => [k, f[k]]));

const SaveState = ({ state }) => {
  const map = { saving: [Loader2, "Enregistrement…", "text-cyan-300 animate-spin"], saved: [Check, "Enregistré", "text-emerald-300"],
    error: [CloudOff, "Échec de l'enregistrement – nouvelle tentative automatique", "text-red-300"] };
  if (!map[state]) return null;
  const [Icon, label, cls] = map[state];
  return <span className="flex items-center gap-1 text-xs text-slate-300" data-testid="wizard-save-state"><Icon className={`h-3.5 w-3.5 ${cls}`} />{label}</span>;
};

const Progress = ({ step, setStep }) => (
  <ol className="grid grid-cols-5 gap-1" data-testid="wizard-progress">
    {STEPS.map((s, i) => (
      <li key={s}>
        <button type="button" onClick={() => setStep(i)} data-testid={`wizard-step-${i}`}
          className={`w-full rounded-md border-t-4 px-1 pt-2 text-left text-[11px] sm:text-sm ${i === step ? "border-cyan-400 text-white" : i < step ? "border-cyan-700 text-slate-300" : "border-white/10 text-slate-500"}`}>
          <span className="hidden sm:inline">{i + 1}. </span>{s}
        </button>
      </li>
    ))}
  </ol>
);

const QuickClient = ({ onCreated }) => {
  const [f, setF] = useState(null);
  if (!f) return <Button type="button" variant="secondary" size="sm" onClick={() => setF({ name: "", type: "particulier", phone: "", email: "" })} data-testid="wizard-new-client-button"><UserPlus className="mr-1 h-4 w-4" />Nouveau client</Button>;
  const save = async () => {
    try { const { data } = await api.post("/clients", f); toast.success("Client créé"); onCreated(data); setF(null); } catch (e) { toast.error(formatApiError(e)); }
  };
  const s = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  return (
    <div className="grid gap-2 rounded-lg border border-cyan-400/20 p-3 sm:grid-cols-2" data-testid="wizard-new-client-form">
      <Input placeholder="Nom *" value={f.name} onChange={s("name")} data-testid="wizard-client-name-input" />
      <SelectField value={f.type} onChange={s("type")} options={{ particulier: "Particulier", professionnel: "Professionnel" }} testid="wizard-client-type-select" />
      <Input placeholder="Téléphone" value={f.phone} onChange={s("phone")} data-testid="wizard-client-phone-input" />
      <Input placeholder="E-mail" type="email" value={f.email} onChange={s("email")} data-testid="wizard-client-email-input" />
      <div className="flex gap-2 sm:col-span-2"><Button size="sm" disabled={!f.name.trim()} onClick={save} data-testid="wizard-client-save-button">Créer</Button><Button size="sm" variant="ghost" onClick={() => setF(null)}>Annuler</Button></div>
    </div>
  );
};

const StepClient = ({ f, set, clients, overview, reloadClients }) => (
  <div className="space-y-4">
    <Field label="Client"><SelectField value={f.client_id} onChange={set("client_id")} options={clients.map((c) => [c.id, c.name])} testid="wizard-client-select" placeholder="Choisir un client" /></Field>
    <QuickClient onCreated={(c) => { reloadClients(); set("client_id")(c.id); }} />
    {overview && (
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-white/10 p-3" data-testid="wizard-client-contracts">
          <p className="mb-1 text-xs uppercase text-slate-400">Contrats actifs</p>
          {overview.contracts.filter((c) => ["actif", "a_renouveler"].includes(c.effective_status)).map((c) => (
            <p key={c.id} className="text-sm text-slate-200">{c.name} · {CONTRACT_TYPES[c.type]}{c.current.available != null && ` · reste ${fmtMin(c.current.remaining)}`}</p>
          ))}
          {!overview.contracts.some((c) => ["actif", "a_renouveler"].includes(c.effective_status)) && <p className="text-sm text-slate-500">Aucun</p>}
        </div>
        <div className="rounded-lg border border-white/10 p-3" data-testid="wizard-client-recent">
          <p className="mb-1 text-xs uppercase text-slate-400">Dernières interventions</p>
          {overview.recent.map((r) => <p key={r.id} className="truncate text-sm text-slate-200">{fmtDate(r.date)} · {r.title}</p>)}
          {!overview.recent.length && <p className="text-sm text-slate-500">Aucune</p>}
        </div>
      </div>
    )}
  </div>
);

const StepEquipment = ({ f, set, overview, onAdd }) => {
  const list = overview?.equipment || [];
  const covered = new Set((overview?.contracts || []).filter((c) => ["actif", "a_renouveler"].includes(c.effective_status)).flatMap((c) => c.equipment_ids || []));
  const lastFor = (id) => overview?.recent.find((r) => r.equipment_ids.includes(id));
  const toggle = (id) => set("equipment_ids")(f.equipment_ids.includes(id) ? f.equipment_ids.filter((x) => x !== id) : [...f.equipment_ids, id]);
  return (
    <div className="space-y-3">
      {!f.client_id && <p className="text-sm text-slate-400">Choisissez d'abord un client (étape 1).</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        {list.map((e) => (
          <label key={e.id} className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${f.equipment_ids.includes(e.id) ? "border-cyan-400 bg-cyan-400/10" : "border-white/10"}`} data-testid={`wizard-equipment-${e.id}`}>
            <input type="checkbox" checked={f.equipment_ids.includes(e.id)} onChange={() => toggle(e.id)} className="mt-1" />
            <span className="min-w-0 text-sm">
              <span className="block font-medium text-white">{e.name || `${e.brand} ${e.model}`}</span>
              <span className="block text-xs text-slate-400">{EQUIPMENT_TYPES[e.type]} {e.serial && `· n° ${e.serial}`}</span>
              <span className={`block text-xs ${covered.has(e.id) ? "text-emerald-300" : "text-slate-500"}`}>{covered.has(e.id) ? "Couvert par un contrat" : "Non couvert"}</span>
              {lastFor(e.id) && <span className="block truncate text-xs text-slate-400">Dernière : {lastFor(e.id).date} · {lastFor(e.id).title}</span>}
            </span>
          </label>
        ))}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="secondary" size="sm" disabled={!f.client_id} onClick={onAdd} data-testid="wizard-add-equipment-button"><Plus className="mr-1 h-4 w-4" />Ajouter un équipement</Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => set("equipment_ids")([])} data-testid="wizard-no-equipment-button">Aucun équipement concerné</Button>
      </div>
    </div>
  );
};

const StepPlanning = ({ f, set, users, overview }) => {
  const active = (overview?.contracts || []).filter((c) => ["actif", "a_renouveler"].includes(c.effective_status));
  const chosen = active.find((c) => c.id === f.contract_id);
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <ModeBlock f={f} set={set} testid="wizard-mode" />
      <Field label="Date et heure"><Input type="datetime-local" value={f.scheduled_at || ""} onChange={(e) => set("scheduled_at")(e.target.value)} data-testid="wizard-date-input" /></Field>
      <Field label="Durée prévue (min)"><Input type="number" min={15} step={15} value={f.duration_minutes} onChange={(e) => set("duration_minutes")(Number(e.target.value))} data-testid="wizard-duration-input" /></Field>
      <Field label="Type"><SelectField value={f.type} onChange={set("type")} options={INTERVENTION_TYPES} testid="wizard-type-select" /></Field>
      <Field label="Priorité"><SelectField value={f.priority} onChange={set("priority")} options={PRIORITIES} testid="wizard-priority-select" /></Field>
      <Field label="Technicien"><SelectField value={f.technician_id} onChange={set("technician_id")} options={users.map((u) => [u.id, u.name || u.email])} allowNone="Non assigné" testid="wizard-technician-select" /></Field>
      {modeOf(f) === "site" && <Field label="Adresse"><Input value={f.location} onChange={(e) => set("location")(e.target.value)} placeholder="Vide = adresse du client" data-testid="wizard-location-input" /></Field>}
      <Field label="Contrat" className="sm:col-span-2">
        <SelectField value={f.contract_id} onChange={set("contract_id")} options={active.map((c) => [c.id, `${c.name} (${c.number})`])} allowNone="Intervention hors contrat" testid="wizard-contract-select" />
        {chosen && (
          <p className="mt-1 text-xs text-slate-300" data-testid="wizard-contract-info">
            {chosen.current.available != null ? `Heures restantes : ${fmtMin(chosen.current.remaining)} sur ${fmtMin(chosen.current.available)}` : "Prestations au forfait"}
            {chosen.included && ` · Inclus : ${chosen.included.slice(0, 120)}`}
            {f.equipment_ids.some((e) => !(chosen.equipment_ids || []).includes(e)) && <span className="block text-amber-300">Un équipement sélectionné n'est pas couvert par ce contrat.</span>}
          </p>
        )}
      </Field>
      <label className="flex items-center gap-2 text-sm text-slate-200 sm:col-span-2">
        <Switch checked={f.notify_client !== false} onCheckedChange={set("notify_client")} data-testid="wizard-notify-switch" />Notifier le client par e-mail après validation
      </label>
    </div>
  );
};

const StepWork = ({ f, set, id }) => (
  <div className="space-y-4">
    <Field label="Objet *"><Input value={f.title} onChange={(e) => set("title")(e.target.value)} placeholder="Ex. PC lent, installation box…" data-testid="wizard-title-input" /></Field>
    <Field label="Demande du client"><Textarea value={f.description} onChange={(e) => set("description")(e.target.value)} data-testid="wizard-description-input" /></Field>
    <Field label="Diagnostic"><Textarea value={f.diagnostic} onChange={(e) => set("diagnostic")(e.target.value)} data-testid="wizard-diagnostic-input" /></Field>
    <Field label="Prestations / travaux réalisés"><Textarea value={f.report} onChange={(e) => set("report")(e.target.value)} data-testid="wizard-report-input" /></Field>
    <Field label="Notes internes"><Textarea value={f.internal_notes} onChange={(e) => set("internal_notes")(e.target.value)} data-testid="wizard-notes-input" /></Field>
    {id ? <BillingPanel interventionId={id} /> : <p className="text-xs text-slate-400">Le matériel utilisé pourra être ajouté dès le premier enregistrement automatique.</p>}
  </div>
);

const workshopRows = (f, eq) => {
  const ws = f.workshop || {};
  return [["Matériel confié", eq.join(", "), "wizard-summary-ws-equipment"],
    ["Accessoires déposés", (ws.accessories || []).join(", ") || "Aucun accessoire", "wizard-summary-ws-accessories"],
    ["État à la réception", ws.condition || "Non renseigné", "wizard-summary-ws-condition"],
    ["Panne signalée", f.description || "Non renseignée", "wizard-summary-ws-issue"],
    ["Date de dépôt", fmtDate(ws.deposit_date || todayStr()), "wizard-summary-ws-deposit"],
    ["Disponibilité prévue", ws.expected_date ? fmtDate(ws.expected_date) : "Non définie", "wizard-summary-ws-expected"]];
};

const SummaryItem = ({ k, v, testid }) => (
  <div className="rounded-lg border border-white/10 p-2" data-testid={testid}>
    <dt className="text-xs text-slate-400">{k}</dt>
    <dd className={`whitespace-pre-line break-words text-sm ${v ? "text-white" : "text-amber-300"}`}>{v || "À compléter"}</dd>
  </div>
);

const Summary = ({ f, clients, overview, users }) => {
  const eq = (overview?.equipment || []).filter((e) => f.equipment_ids.includes(e.id)).map((e) => e.name || `${e.brand} ${e.model}`);
  const atelier = modeOf(f) === "atelier";
  const rows = [["Mode", MODES[modeOf(f)], "wizard-summary-mode"], ["Client", clients.find((c) => c.id === f.client_id)?.name], ["Objet", f.title], ["Type", INTERVENTION_TYPES[f.type]],
    [atelier ? "Rendez-vous" : "Date", f.scheduled_at ? fmtDateTime(f.scheduled_at) : atelier ? "Sans rendez-vous" : "À planifier"], ["Technicien", users.find((u) => u.id === f.technician_id)?.name],
    ...(atelier ? [] : [["Équipements", eq.join(", ") || "Aucun"]]), ["Contrat", (overview?.contracts || []).find((c) => c.id === f.contract_id)?.name || "Hors contrat"]];
  return (
    <div className="space-y-3" data-testid="wizard-summary">
      <dl className="grid gap-2 sm:grid-cols-2">{rows.map(([k, v, t]) => <SummaryItem key={k} k={k} v={v} testid={t} />)}</dl>
      {atelier && (
        <section className="rounded-xl border border-cyan-400/25 bg-cyan-400/[0.04] p-3" data-testid="wizard-summary-workshop">
          <h3 className="mb-2 text-sm font-semibold text-cyan-200">Dépôt en atelier — à contrôler avec le client</h3>
          {!eq.length && <p className="mb-2 text-sm text-amber-300" data-testid="wizard-summary-ws-warning">Aucun équipement sélectionné : revenez à l'étape Équipement pour indiquer le matériel confié.</p>}
          <dl className="grid gap-2 sm:grid-cols-2">{workshopRows(f, eq).map(([k, v, t]) => <SummaryItem key={k} k={k} v={v} testid={t} />)}</dl>
          <p className="mt-2 text-xs text-slate-400">Le numéro de bon de dépôt (DEP-…) est attribué à l'enregistrement.</p>
        </section>
      )}
    </div>
  );
};

export default function InterventionWizard() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [id, setId] = useState(params.get("id"));
  const [f, setF] = useState({ ...EMPTY, client_id: params.get("client_id") || "", ...(params.get("mode") === "atelier" ? { mode: "atelier", notify_client: false } : {}) });
  const [step, setStep] = useState(0);
  const [save, setSave] = useState("idle");
  const [overview, setOverview] = useState(null);
  const [addEq, setAddEq] = useState(false);
  const dirty = useRef(false);
  const busy = useRef(false);
  const clientsList = useList("/clients");
  const { items: users } = useList("/users");
  const clients = clientsList.items;

  useEffect(() => {
    if (!id) return;
    api.get(`/interventions/${id}`).then((r) => {
      if (!r.data.draft) { nav(`/interventions/${id}`, { replace: true }); return; }
      setF({ ...EMPTY, ...pick(r.data), scheduled_at: (r.data.scheduled_at || "").slice(0, 16), equipment_ids: r.data.equipment_ids || [] });
    }).catch(() => toast.error("Brouillon introuvable"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadOverview = useCallback(() => {
    if (f.client_id) api.get(`/clients/${f.client_id}/overview`).then((r) => setOverview(r.data)); else setOverview(null);
  }, [f.client_id]);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  const latest = useRef(f);
  latest.current = f;
  const persist = useCallback(async (data) => {
    if (busy.current) return false;
    busy.current = true;
    dirty.current = false;
    setSave("saving");
    try {
      if (id) await api.patch(`/interventions/${id}`, { ...pick(data), draft: true });
      else {
        const r = await api.post("/interventions", { ...pick(data), draft: true });
        setId(r.data.id);
        setParams({ id: r.data.id }, { replace: true });
      }
      localStorage.removeItem("dt-wizard-backup");
      setSave("saved");
      return true;
    } catch {
      dirty.current = true;
      localStorage.setItem("dt-wizard-backup", JSON.stringify({ id, f: data }));
      setSave("error");
      return false;
    } finally {
      busy.current = false;
      if (data !== latest.current) dirty.current = true;
    }
  }, [id, setParams]);

  useEffect(() => {
    if (!dirty.current) return undefined;
    const t = setTimeout(() => persist(f), save === "error" ? 5000 : 1200);
    return () => clearTimeout(t);
  }, [f, persist, save]);
  useEffect(() => {
    const retry = () => dirty.current && persist(f);
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, [f, persist]);

  const set = (k) => (v) => {
    dirty.current = true;
    setF((p) => {
      const next = { ...p, [k]: v };
      if (k === "client_id" && v !== p.client_id) { next.equipment_ids = []; next.contract_id = null; }
      return next;
    });
  };

  const validate = async () => {
    if (!f.client_id || !f.title.trim()) { toast.error("Le client et l'objet sont nécessaires pour valider"); setStep(f.client_id ? 3 : 0); return; }
    const client = clients.find((c) => c.id === f.client_id);
    if (f.scheduled_at && f.notify_client !== false && client?.email
      && !window.confirm(`Un e-mail de confirmation sera envoyé à ${client.email}. Continuer ?\n(Annuler puis désactiver « Notifier le client » pour ne rien envoyer.)`)) return;
    try {
      let iid = id;
      if (!iid) iid = (await api.post("/interventions", { ...pick(f), draft: true })).data.id;
      await api.patch(`/interventions/${iid}`, { ...pick(f), draft: false });
      toast.success(f.mode === "atelier" ? "Dépôt en atelier enregistré" : f.scheduled_at ? "Intervention validée et planifiée" : "Intervention validée (à planifier)");
      nav(`/interventions/${iid}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const saveDraft = async () => {
    for (let i = 0; i < 20 && busy.current; i += 1) await new Promise((r) => setTimeout(r, 150));
    if (!(await persist(latest.current))) { toast.error("Enregistrement impossible pour le moment. Vos saisies restent sur cet écran, réessayez."); return; }
    toast.success("Brouillon enregistré");
    nav("/interventions?cat=drafts");
  };

  const steps = [
    <StepClient key="c" f={f} set={set} clients={clients} overview={overview} reloadClients={clientsList.reload} />,
    <StepEquipment key="e" f={f} set={set} overview={overview} onAdd={() => setAddEq(true)} />,
    <StepPlanning key="p" f={f} set={set} users={users} overview={overview} />,
    <StepWork key="w" f={f} set={set} id={id} />,
    <Summary key="s" f={f} clients={clients} overview={overview} users={users} />,
  ];
  return (
    <div className="space-y-5 pb-44 lg:pb-0" data-testid="intervention-wizard">
      <PageHeader title="Création guidée" subtitle="Tout est enregistré en brouillon au fur et à mesure. Aucune étape n'est obligatoire avant la validation."><SaveState state={save} /></PageHeader>
      <Progress step={step} setStep={setStep} />
      <section className="rounded-xl border border-cyan-400/15 bg-[#122549]/60 p-4 sm:p-6">{steps[step]}</section>
      <div className="fixed inset-x-0 bottom-16 z-30 border-t border-cyan-400/15 bg-[#0B1A33]/95 p-3 backdrop-blur lg:static lg:border-0 lg:bg-transparent lg:p-0">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-2">
          <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="wizard-prev-button">Précédent</Button>
          <Button variant="secondary" onClick={saveDraft} data-testid="wizard-save-draft-button">Enregistrer en brouillon</Button>
          {step < 4 ? (
            <Button className="dt-glow ml-auto" onClick={() => setStep(step + 1)} data-testid="wizard-next-button">{step === 1 || step === 2 ? "Suivant / passer" : "Suivant"}</Button>
          ) : (
            <Button className="dt-glow ml-auto" onClick={validate} data-testid="wizard-validate-button">{f.scheduled_at ? "Valider et planifier l'intervention" : "Valider l'intervention"}</Button>
          )}
        </div>
      </div>
      <EquipmentDialog open={addEq} onOpenChange={setAddEq} initial={{ client_id: f.client_id }} fixedClient
        onSaved={(e) => { loadOverview(); set("equipment_ids")([...f.equipment_ids, e.id]); }} />
    </div>
  );
}
